import { Dispatch, FC, SetStateAction, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, useFieldArray } from 'react-hook-form';
import Swal from 'sweetalert2';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { TextField, Grid, FormControl, Select, InputLabel, MenuItem, Tooltip } from '@mui/material';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';

import { SCUIGeneralModal } from '../../../../../../../../atomic/molecules';

import { useRuleService } from '../../../../../../context/RuleServiceContext';
import { getOperators, RuleTenant } from '../../../../../../helper/RuleServiceV2.const';
import { RuleServiceCommand } from '../../../../../../../../services/command';
import { RTTM } from './ruleTemplateModal.classes';

type IProps = {
    isModalOpen: boolean;
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
    initialData?: any;
};

type ICriteriaTypeValue = {
    CriteriaTypeId: number | string;
    Operator: number | string;
};

type IFormData = {
    Name: string;
    criteriaTypeValues: ICriteriaTypeValue[];
};

const defaultValues = {
    Name: '',
    criteriaTypeValues: [],
};

export const RuleTemplateModal: FC<IProps> = ({ isModalOpen, setIsModalOpen, initialData }) => {
    const [criteriaTypes, setCriteriaTypes] = useState<any[]>([]);
    const { t } = useTranslation();
    const { tenant, setIsLoading, fetchRuleTemplateGridData } = useRuleService();
    const { control, register, getValues, reset, watch } = useForm<IFormData>({
        defaultValues,
    });
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'criteriaTypeValues',
    });

    const operators = getOperators();
    const criteriaTypeValues = watch('criteriaTypeValues');

    const handleModalClose = () => {
        setIsModalOpen(false);
        reset(defaultValues);
    };

    const handleModalConfirm = () => {
        onSubmit();
    };

    const onSubmit = () => {
        let currentPayload = getValues();
        if (currentPayload) {
            setIsLoading(true);
            const isUpdate = initialData && initialData.id;
            const requestPayload = {
                RuleTemplate: {
                    Id: isUpdate ? initialData.id : '',
                    Tenant: tenant,
                    Name: currentPayload?.Name,
                    criteriaTypes: currentPayload?.criteriaTypeValues,
                },
            };
            const req = isUpdate
                ? RuleServiceCommand.CreateRuleTemplate(requestPayload)
                : RuleServiceCommand.CreateRuleTemplate(requestPayload);
            req.then((res: any) => {
                if (res && (res.result?.statuscode === 200 || res.result?.StatusCode === 200)) {
                    Swal.fire(t('general.success'), ' ', 'success');
                    setIsModalOpen(false);
                    reset(defaultValues);
                    fetchRuleTemplateGridData();
                } else {
                    Swal.fire(
                        t('general.error'),
                        res?.result?.error || res?.result?.message || t('general.error'),
                        'error'
                    );
                    setIsLoading(false);
                }
            })
                .catch(() => {
                    Swal.fire(t('general.error'), '', 'error');
                    setIsLoading(false);
                })
                .finally(() => {
                    setIsModalOpen(false);
                    setIsLoading(false);
                });
        }
    };

    const getAllCriteriaTypes = useCallback(async () => {
        const response = await RuleServiceCommand.GetCriteriaTypeList({
            Tenant: tenant,
            Skip: 0,
        });
        if (response?.data) {
            setCriteriaTypes(response.data);
        }
    }, [tenant, setCriteriaTypes]);

    useEffect(() => {
        getAllCriteriaTypes();
    }, [getAllCriteriaTypes]);

    useEffect(() => {
        if (initialData) {
            reset({
                Name: initialData.name || '',
                criteriaTypeValues: (initialData.criteriaTypes || []).map((v: any) => ({
                    CriteriaTypeId: criteriaTypes?.find((type: any) => type.key === v.key)?.id,
                    Operator: v.operator,
                })),
            });
        } else {
            reset(defaultValues);
        }
    }, [initialData, reset, isModalOpen]);
    const isEdit = !!(initialData && initialData.id);
    const modalTitle = isEdit
        ? t('ruleManagement.modal.ruleTemplate.editTitle')
        : t('ruleManagement.modal.ruleTemplate.title');
    const buttonText = isEdit ? t('general.update') : t('general.save');
    console.log('criteriaTypes: ', criteriaTypes, 'operators: ', operators, 'formDta: ', getValues());
    return (
        <SCUIGeneralModal
            width={'xs'}
            open={isModalOpen}
            onClose={handleModalClose}
            title={modalTitle}
            functionButtonOnClick={handleModalConfirm}
            buttonText={buttonText}
            rootClassName={RTTM.modalRoot}
            paperClassName={RTTM.modalPaper}
            titleClassName={RTTM.modalTitle}
            closeIconButtonClassName={RTTM.modalCloseButton}
            closeIconClassName={RTTM.modalCloseIcon}
        >
            <form className={RTTM.form}>
                <Grid className={RTTM.formGrid} container sx={{ padding: '2rem' }} style={{ display: 'flex' }}>
                    <FormControl fullWidth>
                        <TextField
                            required
                            className={RTTM.fieldName}
                            sx={{ paddingBottom: '1rem' }}
                            label={t(`ruleManagement.fields.ruleTemplate.name.name`)}
                            multiline
                            {...register('Name')}
                            fullWidth
                        />
                    </FormControl>
                    <FormControl fullWidth>
                        <TextField
                            required
                            className={RTTM.fieldTenant}
                            sx={{ paddingBottom: '1rem' }}
                            value={RuleTenant[+tenant as keyof typeof RuleTenant]}
                            label={'Tenant'}
                            disabled
                            multiline
                            fullWidth
                        />
                    </FormControl>
                    <div className={RTTM.criteriaSection} style={{ display: 'flex', flexDirection: 'column' }}>
                        <div className={RTTM.addCriteriaButtonWrap} style={{ width: 100 }}>
                            <IconButton
                                className={RTTM.addCriteriaButton}
                                size="small"
                                sx={{ paddingTop: '1rem' }}
                                onClick={() => append({ CriteriaTypeId: '', Operator: '' })}
                            >
                                <AddIcon />
                                {t('general.add')}
                            </IconButton>
                        </div>
                        {fields && fields?.length > 0 && (
                            <div className={RTTM.criteriaRows} style={{ display: 'flex', flexDirection: 'column' }}>
                                {(fields || []).map((_: any, index: number) => (
                                    <div
                                        key={index}
                                        className={RTTM.criteriaRow}
                                        data-criteria-index={index}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'start',
                                        }}
                                    >
                                        <FormControl style={{ width: 150, margin: '20px' }}>
                                            <InputLabel
                                                className={RTTM.criteriaTypeLabel}
                                                id={'CriteriaType' + index}
                                            >
                                                {t(`ruleManagement.fields.ruleTemplate.criteriaType.parameter`)}
                                            </InputLabel>
                                            <Select
                                                className={RTTM.criteriaTypeSelect}
                                                labelId={'CriteriaType' + index}
                                                id={'CriteriaType' + index}
                                                size="medium"
                                                value={criteriaTypeValues?.[index]?.CriteriaTypeId}
                                                {...register(`criteriaTypeValues.${index}.CriteriaTypeId`)}
                                                label={t(`ruleManagement.fields.ruleTemplate.criteriaType.parameter`)}
                                            >
                                                {criteriaTypes?.length > 0 &&
                                                    (criteriaTypes || []).map((criteriaType: any) => (
                                                        <MenuItem
                                                            className={RTTM.criteriaTypeSelectItem}
                                                            key={'criteriaType-option-' + criteriaType.id}
                                                            id={criteriaType.id}
                                                            value={criteriaType.id}
                                                            data-criteria-type-id={criteriaType.id}
                                                        >
                                                            {criteriaType.name}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl style={{ width: 150, margin: '20px' }}>
                                            <InputLabel className={RTTM.operatorLabel} id={'Operator'}>
                                                {t(`ruleManagement.operatorV2`)}
                                            </InputLabel>
                                            <Select
                                                className={RTTM.operatorSelect}
                                                labelId={'Operator'}
                                                id={'Operator'}
                                                value={criteriaTypeValues?.[index]?.Operator}
                                                {...register(`criteriaTypeValues.${index}.Operator`)}
                                                size="medium"
                                                label={t(`ruleManagement.operatorV2`)}
                                            >
                                                {operators &&
                                                    operators.map((operator: any, index: any) => (
                                                        <MenuItem
                                                            className={RTTM.operatorSelectItem}
                                                            id={operator.value}
                                                            value={operator.value}
                                                            key={'operator-option-' + index}
                                                            data-operator-value={operator.value}
                                                        >
                                                            {operator.label}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                        <IconButton
                                            className={RTTM.removeCriteriaButton}
                                            size="small"
                                            onClick={() => remove(index)}
                                        >
                                            <CloseIcon className={RTTM.removeCriteriaIcon} />
                                        </IconButton>
                                    </div>
                                ))}
                                <Tooltip className={RTTM.operatorsTooltip} title={t('ruleManagement.operators.tooltipInfo')}>
                                    <IconButton
                                        className={RTTM.operatorsHelpButton}
                                        size="large"
                                        sx={{
                                            marginLeft: 'auto',
                                            marginRight: '36px',
                                            width: 'fit-content',
                                            padding: '0',
                                        }}
                                    >
                                        <HelpOutlineOutlinedIcon className={RTTM.operatorsHelpIcon} sx={{ width: '25px', height: '25px' }} />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        )}
                    </div>
                </Grid>
            </form>
        </SCUIGeneralModal>
    );
};
